import { PM_PROVIDERS, PROVIDERS_MAP } from 'common/constants/constants';
import { API, LINB_API } from '../constants/api-urls';
import axios from '../requester';
import _ from 'lodash';
import { getAllEntitiesByEntityType } from 'components/FilterMenus/FilterMenus.service';
import { FILTER_TYPES } from 'common/constants/filters';

export const formatFiltersFromDynamoDB = (entities = []) => {
  return entities.map(({ entity_id, value, extra }) => ({
    id: entity_id,
    name: value,
    ...extra
  }));
};

export const getPMIssuesType = async (cachePolicy = 'no-cache') => {
  const headers = {
    'Cache-Control': cachePolicy
  };
  const issuesTypes = await axios.get(
    `${API.PM_PROVIDER}/meta_data/issue_types`,
    headers
  );
  return issuesTypes;
};

export const getPMData = async (isAuthorized = true, sensorId) => {
  if (!isAuthorized) {
    return {
      issueTypes: [],
      labels: [],
      fixVersions: [],
      customFields: [],
      statuses: []
    };
  }
  const isClubhouse = sensorId === PM_PROVIDERS.CLUBHOUSE;
  if (!isClubhouse) {
    const [issueTypes, labels, fixVersions, customFields, statuses] =
      await Promise.allSettled([
        getAllEntitiesByEntityType(sensorId, FILTER_TYPES.ISSUE_TYPES),
        axios.get(LINB_API.PM_SERVICE.PM_ENTITIES.LABELS),
        axios.get(LINB_API.PM_SERVICE.PM_ENTITIES.FIX_VERSIONS),
        getAllEntitiesByEntityType(sensorId, FILTER_TYPES.FIELD),
        axios.get(LINB_API.PM_SERVICE.PM_ENTITIES.STATUSES)
      ]);
    return {
      issueTypes: formatFiltersFromDynamoDB(
        _.get(issueTypes, 'value.items', [])
      ),
      labels: formatFiltersFromDynamoDB(_.get(labels, 'value.items', [])),
      fixVersions: formatFiltersFromDynamoDB(
        _.get(fixVersions, 'value.items', [])
      ),
      customFields: formatFiltersFromDynamoDB(
        _.get(customFields, 'value.items', [])
      ),
      statuses: formatFiltersFromDynamoDB(_.get(statuses, 'value.items', []))
    };
  }
  const [issuesTypes, labels, fixVersions, customFields, statuses] =
    await Promise.allSettled([
      getPMIssuesType(),
      axios.get(`${API.PM_PROVIDER}/meta_data/labels`),
      axios.get(`${API.PM_PROVIDER}/meta_data/fix_versions`),
      axios.get(`${API.PM_PROVIDER}/meta_data/fields`),
      axios.get(`${API.PM_PROVIDER}/meta_data/statuses`)
    ]);
  return {
    issueTypes: _.get(issuesTypes, 'value.items', []),
    labels: _.get(labels, 'value.items', []),
    fixVersions: _.get(fixVersions, 'value.items', []),
    customFields: _.get(customFields, 'value.items', []),
    statuses: _.get(statuses, 'value.items', [])
  };
};

export const extractSelectedEntitiesFromFilter = {
  [PM_PROVIDERS.JIRA]: (filter, entityName = PROVIDERS_MAP.jira.entity_name) =>
    _.get(filter, PROVIDERS_MAP.jira.entities_settings_keys[entityName], []),
  [PM_PROVIDERS.CLUBHOUSE]: filter => {
    const entityTypes = _.values(
      PROVIDERS_MAP.clubhouse.entities_settings_keys
    );
    const selectedEntities = entityTypes.reduce((acc, entityType) => {
      const currentEntities = _.get(filter, entityType, []);
      return [...acc, ...currentEntities];
    }, []);
    return selectedEntities;
  },
  [PM_PROVIDERS.AZURE_BOARDS]: (filter, entityName) => {
    if (entityName && entityName in filter) return filter[entityName] || [];

    const entityTypes = _.values(
      PROVIDERS_MAP.azure_boards.entities_settings_keys
    );
    const selectedEntities = entityTypes.reduce((acc, entityType) => {
      const currentEntities = _.get(filter, entityType, []);
      return [...acc, ...currentEntities];
    }, []);
    return selectedEntities;
  }
};

/**
 * function gets boards ids and projects from pm import strategy
 * check what projects associated with those boards and return an updated
 * projects array to pm import strategy
 * @param {Array<string>} boardsIds - Array of board IDs
 * @param {Array<object>} projects - Array of projects
 * @param {object} allBoardsByProviderIds - Objects of boards by provider IDs as keys - use boardsProviderKeysSelector
 * @returns {Array<object>} Updated projects array for pm import strategy
 */
export const getProjectsToAddToPmImportStrategyByBoardsIds = (
  boardsIds = [],
  projects = [],
  allBoardsByProviderIds = {}
) => {
  const projectsFromSelectedBoards = boardsIds.map(
    boardId => allBoardsByProviderIds[boardId]?.provider_project_id
  );

  const projectsProviderIdsFromPmImportStrategy = projects.map(
    ({ provider_id }) => provider_id
  );

  const newProjectsToPmImportStrategy = [
    ...new Set([
      ...projectsProviderIdsFromPmImportStrategy,
      ...projectsFromSelectedBoards
    ])
  ];

  return newProjectsToPmImportStrategy
    .filter(Boolean)
    .map(provider_id => ({ provider_id }));
};

export const getPmStatuses = async () =>
  await axios.get(LINB_API.PM_SERVICE.PM_ENTITIES.STATUSES);
